import { useTranslation } from "@sagot/i18n";
import { Checkbox, Field, Icon, Input, RadioGroup, Select, Switch, Textarea } from "@sagot/ui";
import { useId } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useAssignableMembers, useTeams } from "../shared/queries";
import { DefaultWorkflowNote } from "../../engage/workflows/defaults/default-note";
import { MandatoryHandoverCard } from "./mandatory-handover-card";
import { LIMITS, type SettingsValues } from "./settings-model";
import { SettingsCard, SwitchRow } from "./settings-ui";

const OFFICE_HOURS_VARIABLE = "{{office_hours}}";

function Trigger({ name, label, hint, disabled }: { name: "on_request_human" | "payment_topics" | "negative_sentiment"; label: string; hint: string; disabled: boolean }) {
  const { control } = useFormContext<SettingsValues>();
  return (
    <Controller control={control} name={name} render={({ field }) => (
      <Checkbox checked={field.value} onCheckedChange={(v) => { field.onChange(v === true); }} disabled={disabled} label={label} description={hint} />
    )} />
  );
}

function OfficeHours({ disabled, timezone }: { disabled: boolean; timezone: string }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const days = useWatch({ control: form.control, name: "days" });
  return (
    <SettingsCard title={t("build.agentSettings.escalation.hours")} hint={t("build.agentSettings.escalation.hoursHint", { timezone, variable: OFFICE_HOURS_VARIABLE })} flush>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t("build.agentSettings.escalation.day")}</th>
              <th>{t("build.agentSettings.escalation.open")}</th>
              <th>{t("build.agentSettings.escalation.from")}</th>
              <th>{t("build.agentSettings.escalation.to")}</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day, index) => {
              const name = t(`build.agentSettings.escalation.days.${day.day}`);
              const invalid = day.open && day.opens_at >= day.closes_at;
              return (
                <tr key={day.day}>
                  <td className={day.open ? "font-semibold" : "font-semibold text-fg-muted"}>{name}</td>
                  <td>
                    <Controller control={form.control} name={`days.${index}.open`} render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={disabled} aria-label={t("build.agentSettings.escalation.openLabel", { day: name })} />
                    )} />
                  </td>
                  <td><Input type="time" className="h-9 w-32" disabled={disabled || !day.open} aria-label={t("build.agentSettings.escalation.fromLabel", { day: name })} invalid={invalid} {...form.register(`days.${index}.opens_at`)} /></td>
                  <td><Input type="time" className="h-9 w-32" disabled={disabled || !day.open} aria-label={t("build.agentSettings.escalation.toLabel", { day: name })} invalid={invalid} {...form.register(`days.${index}.closes_at`)} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {form.formState.errors.days ? <p className="px-5 pt-3 text-xs font-semibold text-danger" role="alert">{t("build.agentSettings.escalation.hoursInvalid")}</p> : null}
      <div className="border-t border-line p-5">
        <Field label={t("build.agentSettings.escalation.outOfHours")}>
          <Textarea rows={2} maxLength={2000} disabled={disabled} {...form.register("out_of_hours_message")} />
        </Field>
      </div>
    </SettingsCard>
  );
}

function RouteChoice({ disabled, peopleUnavailable }: { disabled: boolean; peopleUnavailable: boolean }) {
  const { t } = useTranslation();
  const { control } = useFormContext<SettingsValues>();
  const labelId = useId();
  return (
    <div>
      <div id={labelId} className="label">{t("build.agentSettings.escalation.routeTo")}</div>
      <Controller control={control} name="route_to" render={({ field }) => (
        <RadioGroup
          variant="cards"
          aria-labelledby={labelId}
          className="mt-1 grid-cols-1 sm:grid-cols-2"
          disabled={disabled}
          value={field.value}
          onValueChange={field.onChange}
          options={[
            { value: "team", label: t("build.agentSettings.escalation.routeTeam"), description: t("build.agentSettings.escalation.routeTeamHint") },
            { value: "person", label: t("build.agentSettings.escalation.routePerson"), description: peopleUnavailable ? t("build.agentSettings.escalation.membersUnavailable") : t("build.agentSettings.escalation.routePersonHint"), disabled: peopleUnavailable },
          ]}
        />
      )} />
    </div>
  );
}

/** "Remind the team after X minutes"; the Owner and Admins are told after twice that. */
function ReminderWait({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const minutes = useWatch({ control: form.control, name: "reminder_minutes" });
  const hint = Number.isInteger(minutes) ? t("build.agentSettings.escalation.reminderHint", { minutes, escalate: minutes * 2 }) : t("build.agentSettings.escalation.reminderHintShort");
  return (
    <Field label={t("build.agentSettings.escalation.reminder")} hint={hint} error={form.formState.errors.reminder_minutes?.message}>
      <Input type="number" inputMode="numeric" min={LIMITS.reminderMin} max={LIMITS.reminderMax} step={1} className="w-28" disabled={disabled} {...form.register("reminder_minutes", { valueAsNumber: true })} />
    </Field>
  );
}

function Routing({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const teams = useTeams();
  const members = useAssignableMembers();
  const routeTo = useWatch({ control: form.control, name: "route_to" });
  const paymentOn = useWatch({ control: form.control, name: "payment_topics" });
  const chosen = useWatch({ control: form.control, name: "default_team_id" });
  const memberCount = teams.data?.find((team) => team.id === chosen)?.member_ids.length;
  const teamOptions = (empty: string) => (
    <>
      <option value="">{empty}</option>
      {(teams.data ?? []).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
    </>
  );
  const teamsLocked = disabled || teams.isError;
  return (
    <SettingsCard title={t("build.agentSettings.escalation.routing")} hint={t("build.agentSettings.escalation.routingHint")}>
      <DefaultWorkflowNote systemKey="handoff_route_and_alert" textKey="engage.workflows.defaults.cross.handoffSettings" />
      {teams.isError ? <p className="text-xs text-fg-muted">{t("build.agentSettings.escalation.teamsUnavailable")}</p> : null}
      <RouteChoice disabled={disabled} peopleUnavailable={members.isError} />
      {routeTo === "person" ? (
        <Field label={t("build.agentSettings.escalation.person")} hint={t("build.agentSettings.escalation.personHint")} error={form.formState.errors.default_assignee_user_id?.message}>
          <Select disabled={disabled || members.isError} {...form.register("default_assignee_user_id")}>
            <option value="">{t("build.agentSettings.escalation.choosePerson")}</option>
            {(members.data ?? []).map((member) => <option key={member.user_id} value={member.user_id}>{member.name}</option>)}
          </Select>
        </Field>
      ) : (
        <Field label={t("build.agentSettings.escalation.team")} hint={memberCount === undefined ? t("build.agentSettings.escalation.teamHint") : t("build.agentSettings.escalation.membersCount", { count: memberCount })}>
          <Select disabled={teamsLocked} {...form.register("default_team_id")}>{teamOptions(t("build.agentSettings.escalation.noTeam"))}</Select>
        </Field>
      )}
      <Field label={t("build.agentSettings.escalation.paymentTeam")} hint={paymentOn ? t("build.agentSettings.escalation.paymentTeamHint") : t("build.agentSettings.escalation.paymentTeamOff")}>
        <Select disabled={teamsLocked || !paymentOn} {...form.register("payment_team_id")}>{teamOptions(t("build.agentSettings.escalation.sameAsDefault"))}</Select>
      </Field>
      <SwitchRow name="match_team_topics" label={t("build.agentSettings.escalation.matchTopics")} hint={t("build.agentSettings.escalation.matchTopicsHint")} disabled={disabled} className="" />
      <p className="rounded-md bg-sunken px-3 py-2 text-xs text-fg-muted">
        <Icon name="info" className="mr-1.5 inline size-3.5 align-text-bottom" />
        {t("build.agentSettings.escalation.fallbackNote")}
      </p>
      <SwitchRow name="collect_contact" label={t("build.agentSettings.escalation.collect")} hint={t("build.agentSettings.escalation.collectHint")} disabled={disabled} className="" />
      <ReminderWait disabled={disabled} />
    </SettingsCard>
  );
}

export function EscalationTab({ disabled, timezone }: { disabled: boolean; timezone: string }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  return (
    <>
      <SettingsCard title={t("build.agentSettings.escalation.triggers")} hint={t("build.agentSettings.escalation.triggersHint")}>
        <div className="space-y-4">
          <Trigger name="on_request_human" label={t("build.agentSettings.escalation.askHuman")} hint={t("build.agentSettings.escalation.askHumanHint")} disabled={disabled} />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold">{t("build.agentSettings.escalation.lowConfidence")}</div>
              <p className="text-xs text-fg-subtle">{t("build.agentSettings.escalation.lowConfidenceHint")}</p>
            </div>
            <Select aria-label={t("build.agentSettings.escalation.strikes")} className="w-24" disabled={disabled} {...form.register("low_confidence_strikes", { valueAsNumber: true })}>
              {Array.from({ length: LIMITS.strikesMax - LIMITS.strikesMin + 1 }, (_, i) => i + LIMITS.strikesMin).map((n) => <option key={n} value={n}>{`${String(n)}×`}</option>)}
            </Select>
          </div>
          <Trigger name="payment_topics" label={t("build.agentSettings.escalation.payment")} hint={t("build.agentSettings.escalation.paymentHint")} disabled={disabled} />
          <Trigger name="negative_sentiment" label={t("build.agentSettings.escalation.sentiment")} hint={t("build.agentSettings.escalation.sentimentHint")} disabled={disabled} />
          <Field label={t("build.agentSettings.escalation.keywords")} hint={t("build.agentSettings.escalation.keywordsHint")}>
            <Input disabled={disabled} {...form.register("keywords")} />
          </Field>
        </div>
      </SettingsCard>
      <Routing disabled={disabled} />
      <MandatoryHandoverCard disabled={disabled} />
      <OfficeHours disabled={disabled} timezone={timezone} />
    </>
  );
}
